import React from 'react';
import { useTranslation } from 'react-i18next';
import './FsrsExplainer.css';

/**
 * Editorial FSRS explainer. Typography-led hierarchy (no colored backgrounds
 * or accent rails); the forgetting curve is the single visual anchor, and
 * purple is reserved for that chart only.
 */
const FsrsExplainer: React.FC = () => {
    const { t } = useTranslation('knowledgeGraph');
    const terms = t('fsrs.terms.items', { returnObjects: true }) as Array<{
        term: string;
        desc: string;
    }>;

    return (
        <section id="fsrs" className="kg-fsrs">
            <header className="kg-fsrs__header">
                <h2 className="kg-fsrs__title">{t('fsrs.title')}</h2>
                <p className="kg-fsrs__subtitle">{t('fsrs.subtitle')}</p>
            </header>

            <ol className="kg-fsrs__beats">
                <li className="kg-fsrs-beat">
                    <div className="kg-fsrs-beat__num">01</div>
                    <div className="kg-fsrs-beat__body">
                        <h3 className="kg-fsrs-beat__title">{t('fsrs.cards.problem.title')}</h3>
                        <p className="kg-fsrs-beat__text">{t('fsrs.cards.problem.body')}</p>
                        <figure className="kg-fsrs-curve" aria-hidden="true">
                            <svg viewBox="0 0 480 210" xmlns="http://www.w3.org/2000/svg">
                                {/* axes */}
                                <line x1="48" y1="170" x2="460" y2="170" stroke="#e5e7eb" strokeWidth="1" />
                                <line x1="48" y1="20" x2="48" y2="170" stroke="#e5e7eb" strokeWidth="1" />
                                {/* 100% dashed reference */}
                                <line x1="48" y1="28" x2="460" y2="28" stroke="#f3f4f6" strokeWidth="1" strokeDasharray="3 4" />
                                {/* curve: 100% → ~50% @ 1hr → ~35% @ 1day → ~21% @ 1week */}
                                <path
                                    d="M 48 28 C 100 28, 130 90, 180 115 S 270 138, 300 143 S 400 164, 430 168"
                                    fill="none"
                                    stroke="#6d28d9"
                                    strokeWidth="2.25"
                                    strokeLinecap="round"
                                />
                                {/* data points */}
                                <circle cx="48" cy="28" r="3.5" fill="#6d28d9" />
                                <circle cx="180" cy="115" r="3.5" fill="#6d28d9" />
                                <circle cx="300" cy="143" r="3.5" fill="#6d28d9" />
                                <circle cx="430" cy="168" r="3.5" fill="#6d28d9" />
                                {/* y labels */}
                                <text x="42" y="31" textAnchor="end" fontSize="11" fill="#9ca3af">100%</text>
                                <text x="42" y="173" textAnchor="end" fontSize="11" fill="#9ca3af">20%</text>
                                {/* x labels */}
                                <text x="48" y="190" fontSize="11" fill="#6b7280">{t('fsrs.curve.now')}</text>
                                <text x="180" y="190" fontSize="11" fill="#6b7280" textAnchor="middle">{t('fsrs.curve.oneHour')}</text>
                                <text x="300" y="190" fontSize="11" fill="#6b7280" textAnchor="middle">{t('fsrs.curve.oneDay')}</text>
                                <text x="430" y="190" fontSize="11" fill="#6b7280" textAnchor="end">{t('fsrs.curve.oneWeek')}</text>
                            </svg>
                            <figcaption className="kg-fsrs-curve__caption">
                                {t('fsrs.curve.caption')}
                            </figcaption>
                        </figure>
                    </div>
                </li>

                <li className="kg-fsrs-beat kg-fsrs-beat--hero">
                    <div className="kg-fsrs-beat__num">02</div>
                    <div className="kg-fsrs-beat__body">
                        <h3 className="kg-fsrs-beat__title">{t('fsrs.cards.solution.title')}</h3>
                        <p className="kg-fsrs-beat__text kg-fsrs-beat__text--lead">
                            {t('fsrs.cards.solution.body')}
                        </p>
                        <blockquote className="kg-fsrs-pull">
                            {t('fsrs.cards.solution.pull')}
                        </blockquote>
                    </div>
                </li>

                <li className="kg-fsrs-beat">
                    <div className="kg-fsrs-beat__num">03</div>
                    <div className="kg-fsrs-beat__body">
                        <h3 className="kg-fsrs-beat__title">{t('fsrs.cards.health.title')}</h3>
                        <p className="kg-fsrs-beat__text">{t('fsrs.cards.health.body')}</p>
                        <ul className="kg-fsrs-states">
                            <li className="kg-fsrs-state kg-fsrs-state--solid">
                                <span className="kg-fsrs-state__dot" aria-hidden="true" />
                                {t('fsrs.cards.health.chipSolid')}
                            </li>
                            <li className="kg-fsrs-state kg-fsrs-state--review">
                                <span className="kg-fsrs-state__dot" aria-hidden="true" />
                                {t('fsrs.cards.health.chipReview')}
                            </li>
                            <li className="kg-fsrs-state kg-fsrs-state--fading">
                                <span className="kg-fsrs-state__dot" aria-hidden="true" />
                                {t('fsrs.cards.health.chipFading')}
                            </li>
                        </ul>
                    </div>
                </li>
            </ol>

            <footer className="kg-fsrs__terms">
                <h4 className="kg-fsrs__terms-title">{t('fsrs.terms.title')}</h4>
                <dl className="kg-fsrs__terms-list">
                    {Array.isArray(terms) &&
                        terms.map((item, idx) => (
                            <div className="kg-fsrs__term" key={idx}>
                                <dt>{item.term}</dt>
                                <dd>{item.desc}</dd>
                            </div>
                        ))}
                </dl>
            </footer>
        </section>
    );
};

export default FsrsExplainer;
